"use client";

import { useMemo, useState, useRef, createContext, useContext } from "react";
import { parseKeyValue } from "@/features/tags/utils/tag-utils";
import type { Tag } from "tag-api-client";
import { XIcon, CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface CustomTagFilterProps {
    tags: Tag[];
    selectedTagIds: string[];
    onSelectedTagsChange: (tagIds: string[]) => void;
    children?: React.ReactNode;
}

interface TagFilterContextValue {
    tags: Tag[];
    selectedTagIds: string[];
    onSelectedTagsChange: (tagIds: string[]) => void;
    inputValue: string;
    setInputValue: (value: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    toggleTag: (tagId: string) => void;
    removeTag: (tagId: string) => void;
    groupedTags: [string, Tag[]][];
    filteredGroupedTags: [string, Tag[]][];
    selectedTags: Tag[];
}

const TagFilterContext = createContext<TagFilterContextValue | null>(null);

const useTagFilterContext = () => {
    const context = useContext(TagFilterContext);
    if (!context) throw new Error("Must be used within CustomTagFilter");
    return context;
};

export function CustomTagFilter({
    tags,
    selectedTagIds,
    onSelectedTagsChange,
    children,
}: CustomTagFilterProps) {
    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);

    const groupedTags = useMemo(() => {
        const groups = new Map<string, Tag[]>();
        tags.forEach((tag) => {
            const parsed = parseKeyValue(tag.keyValue);
            if (parsed) {
                if (!groups.has(parsed.key)) groups.set(parsed.key, []);
                groups.get(parsed.key)!.push(tag);
            }
        });
        return Array.from(groups.entries()).sort(([a], [b]) =>
            a.localeCompare(b),
        );
    }, [tags]);

    const filteredGroupedTags = useMemo(() => {
        if (!inputValue) return groupedTags;
        const lower = inputValue.toLowerCase();
        return groupedTags
            .map(([key, groupTags]) => {
                const filteredTags = groupTags.filter((tag) => {
                    const parsed = parseKeyValue(tag.keyValue);
                    return (
                        key.toLowerCase().includes(lower) ||
                        parsed?.value.toLowerCase().includes(lower)
                    );
                });
                return [key, filteredTags] as [string, Tag[]];
            })
            .filter(([, groupTags]) => groupTags.length > 0);
    }, [groupedTags, inputValue]);

    const selectedTags = useMemo(
        () =>
            [...tags]
                .filter((tag) => selectedTagIds.includes(tag.tagId!))
                .sort((a, b) => {
                    const parsedA = parseKeyValue(a.keyValue);
                    const parsedB = parseKeyValue(b.keyValue);
                    if (!parsedA || !parsedB) return 0;
                    const keyCompare = parsedA.key.localeCompare(parsedB.key);
                    if (keyCompare !== 0) return keyCompare;
                    return parsedA.value.localeCompare(parsedB.value);
                }),
        [tags, selectedTagIds],
    );

    const toggleTag = (tagId: string) => {
        if (selectedTagIds.includes(tagId)) {
            onSelectedTagsChange(selectedTagIds.filter((id) => id !== tagId));
        } else {
            onSelectedTagsChange([...selectedTagIds, tagId]);
        }
    };

    const removeTag = (tagId: string) => {
        onSelectedTagsChange(selectedTagIds.filter((id) => id !== tagId));
    };

    return (
        <TagFilterContext.Provider
            value={{
                tags,
                selectedTagIds,
                onSelectedTagsChange,
                inputValue,
                setInputValue,
                open,
                setOpen,
                toggleTag,
                removeTag,
                groupedTags,
                filteredGroupedTags,
                selectedTags,
            }}
        >
            {children || (
                <div className="flex flex-col gap-2">
                    <CustomTagFilterInput />
                    <CustomTagFilterOutput />
                </div>
            )}
        </TagFilterContext.Provider>
    );
}

export function CustomTagFilterInput({ className }: { className?: string }) {
    const {
        inputValue,
        setInputValue,
        open,
        setOpen,
        filteredGroupedTags,
        toggleTag,
        selectedTagIds,
    } = useTagFilterContext();
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                placeholder="Filter by tags..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={(e) => {
                    if (!e.relatedTarget?.closest("[data-popover-content]")) {
                        setOpen(false);
                    }
                }}
                className={cn(
                    "w-47 h-10 px-2.5 rounded-[var(--color-radius)] border border-md-outline bg-md-surface text-md-on-surface text-sm outline-none focus:border-md-primary focus:ring-[3px] focus:ring-md-primary/20 transition-all hover:border-md-primary hover:bg-md-tertiary-container/10 placeholder:text-gray-600",
                    className,
                )}
            />
            {open && (
                <div
                    data-popover-content
                    className="absolute top-full left-0 mt-1.5 min-w-80 max-w-md max-h-96 overflow-hidden rounded-lg border border-md-outline-variant bg-md-surface shadow-md z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <div className="max-h-96 overflow-y-auto p-1">
                        {filteredGroupedTags.length === 0 ? (
                            <div className="py-6 text-center text-sm text-md-on-surface-variant">
                                No tags found
                            </div>
                        ) : (
                            filteredGroupedTags.map(([key, groupTags]) => (
                                <div key={key}>
                                    <div className="px-2.5 py-1.5 text-xs font-bold text-md-on-surface bg-md-surface-level-1 -mx-1 mb-0.5">
                                        {key}
                                    </div>
                                    {groupTags.map((tag) => {
                                        const parsed = parseKeyValue(
                                            tag.keyValue,
                                        );
                                        const isSelected =
                                            selectedTagIds.includes(tag.tagId!);
                                        return (
                                            <button
                                                key={tag.tagId}
                                                onClick={() =>
                                                    toggleTag(tag.tagId!)
                                                }
                                                className={cn(
                                                    "relative flex w-full items-center gap-2 rounded-md py-1 pr-8 pl-1.5 text-sm text-left outline-none select-none cursor-pointer",
                                                    "hover:bg-md-primary-container hover:text-md-on-primary-container transition-colors duration-150",
                                                    isSelected && "opacity-60",
                                                )}
                                            >
                                                {parsed?.value}
                                                {isSelected && (
                                                    <span className="absolute right-2 flex size-4 items-center justify-center">
                                                        <CheckIcon className="size-4 text-md-primary" />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export function CustomTagFilterOutput() {
    const { selectedTags, removeTag } = useTagFilterContext();

    if (selectedTags.length === 0) return null;

    return (
        <>
            {selectedTags.map((tag) => {
                const parsed = parseKeyValue(tag.keyValue);
                return (
                    <div
                        key={tag.tagId}
                        className="flex h-7 items-center gap-1 rounded-sm bg-md-secondary-container text-md-on-secondary-container px-1.5 text-xs font-medium whitespace-nowrap animate-in fade-in-0 zoom-in-95 duration-150"
                    >
                        {parsed?.key}: {parsed?.value}
                        <button
                            onClick={() => removeTag(tag.tagId!)}
                            className="opacity-50 hover:opacity-100 p-0.5 rounded hover:bg-black/10 transition-opacity duration-150"
                        >
                            <XIcon className="size-3" />
                        </button>
                    </div>
                );
            })}
        </>
    );
}
